import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Folder, Box, Loader2, User, Shield, ExternalLink } from 'lucide-react';
import { api } from '@onvice/portal-shell-api';
import { LucideByName } from '@onvice/portal-ui';

interface ModuleAccess {
  moduleId: string;
  slug: string;
  name: string;
  icon: string;
  level: 'read' | 'write' | 'delete';
  source: 'department' | 'override';
  sourceLabel: string;
}

/**
 * Mapped einen Modul-Slug auf den Uebersicht-Wrapper-Pfad (mit „Als Tab
 * uebernehmen"-Knopf), wo es einen gibt. Module ohne Wrapper bleiben auf
 * der direkten /m/<slug>-Route.
 *
 * Joerg 2026-05-29: Probst soll vom Modul-Picker direkt in den Wrapper
 * landen, damit der Klon-Knopf in der Sub-Tab-Bar sofort sichtbar ist.
 */
const MODULE_TO_UEBERSICHT_ROUTE: Record<string, string> = {
  bauberichteV2: '/uebersicht/aufgaben',
  stammdaten:    '/uebersicht/adressbuch',
  plantafel:     '/uebersicht/plantafel',
  zeiterfassung: '/uebersicht/zeiterfassung',
  bibliothek:    '/uebersicht/bibliothek',
  disposition:   '/uebersicht/auswertungen',
};

function uebersichtRouteFor(slug: string): string {
  return MODULE_TO_UEBERSICHT_ROUTE[slug] ?? `/m/${slug}`;
}

interface DepartmentGroup {
  id: string;
  name: string;
  slug: string;
  icon: string;
  modules: ModuleAccess[];
}

const LEVEL_LABEL: Record<ModuleAccess['level'], string> = {
  read: 'Lesen',
  write: 'Schreiben',
  delete: 'Voll',
};

export default function HomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [departments, setDepartments] = useState<DepartmentGroup[]>([]);
  const [loading, setLoading] = useState(true);

  // Auto-Routing.
  // - /apps (Joerg 2026-06-02): Modul-Picker wird IMMER gerendert — kein Redirect.
  //   Diese Route ist der bewusste Aufruf, wenn der User vom TopBar-Toggle kommt.
  // - / (Default-Landing): laut Tenant-Setting home_layout auf picker/tabs/dashboard.
  //   Mobile-Verhalten je layout:
  //     * dashboard → Mobile bekommt /dashboard (P4.6, Mobile-Parity)
  //     * tabs     → Mobile bekommt erste Mobile-Tab-Route (bisheriges Verhalten)
  //     * picker   → Picker bleibt sichtbar (auch Mobile)
  useEffect(() => {
    if (location.pathname === '/apps') return; // expliziter Modul-Picker-Aufruf
    const isNarrow = typeof window !== 'undefined' && window.innerWidth < 768;
    // 2026-07-07 (Joerg): Auf Mobile ignoriert das Portal home_layout und geht
    // IMMER auf die erste Mobile-Tab (= Zeiterfassung fuer die Standard-Konfig).
    // Dashboard-Home ist Desktop-Feature — auf dem Handy startet der Anwender
    // sofort im operativen Modul.
    if (isNarrow) {
      api.get<{ success: boolean; data: { tabs: Array<{ slug: string; route: string }> } }>('/portal/home-mobile/tabs')
        .then((r2) => {
          const first = r2.data?.tabs?.[0];
          if (first?.route) navigate(first.route, { replace: true });
        })
        .catch(() => { /* Picker bleibt fail-soft sichtbar */ });
      return;
    }
    // Desktop: laut Tenant-Setting home_layout auf picker/tabs/dashboard.
    api.get<{ success: boolean; data: { home_layout: 'picker' | 'tabs' | 'dashboard' } }>('/portal/tenant/home-layout')
      .then((res) => {
        const layout = res.data?.home_layout ?? 'tabs';
        if (layout === 'picker') return; // Picker bleibt sichtbar
        if (layout === 'dashboard') {
          navigate('/dashboard', { replace: true });
          return;
        }
        navigate('/uebersicht/heute', { replace: true });
      })
      .catch(() => {
        // Fail-soft: Desktop auf /uebersicht/heute
        navigate('/uebersicht/heute', { replace: true });
      });
  }, [navigate, location.pathname]);

  useEffect(() => {
    api.get<{ success: boolean; data: { departments: DepartmentGroup[] } }>('/portal/my-access')
      .then((res) => setDepartments(res.data.departments))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-brand-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-8 py-8 max-w-5xl mx-auto">
      {departments.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          Keine Anwendungen freigeschaltet. Bitte den Administrator kontaktieren.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {departments.map((dept) => {
            const isOverrideGroup = dept.slug === '__overrides__';
            const isRoleGroup = dept.slug === '__role__';
            const deptFallback = isRoleGroup ? Shield : isOverrideGroup ? User : Folder;
            const headline = isRoleGroup ? 'Alle Anwendungen (Administrator)'
                            : isOverrideGroup ? 'Persoenliche Anwendungen'
                                              : dept.name;
            return (
              <div key={dept.id} className="rounded-xl bg-slate-900 border border-slate-800 overflow-hidden">
                <div className="p-4 sm:p-3 border-b border-slate-800 flex items-center gap-3">
                  <div className="w-10 h-10 sm:w-8 sm:h-8 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center">
                    <LucideByName name={dept.icon} fallback={deptFallback} size={18} className="text-brand-400" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-slate-100 sm:text-sm">{headline}</h2>
                    <p className="text-xs text-slate-500">{dept.modules.length} Anwendung{dept.modules.length !== 1 ? 'en' : ''}</p>
                  </div>
                </div>

                <div className="p-2 sm:p-1.5">
                  {dept.modules.map((mod) => (
                    <div
                      key={mod.moduleId}
                      className="flex items-center gap-1 rounded-lg hover:bg-slate-800 transition-colors"
                    >
                      <Link
                        to={uebersichtRouteFor(mod.slug)}
                        className="flex items-center gap-3 p-3 sm:p-2.5 flex-1 min-w-0"
                      >
                        <div className="w-9 h-9 sm:w-7 sm:h-7 rounded-lg bg-brand-500/10 flex items-center justify-center">
                          <LucideByName name={mod.icon} fallback={Box} size={16} className="text-brand-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm sm:text-xs font-medium text-slate-200 truncate">{mod.name}</div>
                          <div className="text-[11px] text-slate-500">{LEVEL_LABEL[mod.level]}</div>
                        </div>
                      </Link>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          window.open(uebersichtRouteFor(mod.slug), `onvice-${mod.slug}`,
                            'popup,width=1280,height=900,menubar=no,toolbar=no,location=no');
                        }}
                        title="In neuem Fenster öffnen"
                        className="hidden sm:flex p-2 mr-1 rounded text-slate-500 hover:text-brand-400 hover:bg-brand-500/10"
                      >
                        <ExternalLink size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
